import { inject, Injectable } from '@angular/core';
import { ICorrelationCurve } from '@app/api/interfaces/chart-data/correlation-curve';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { MeasurementSystem } from '@app/core/enums/measurement-system.enum';
import { DayjsLocaleService } from '@app/core/i18n/services/dayjs-locale.service';
import { measurementSystemLengthUnit_m_ft } from '@app/core/localize/measurement-system-length-unit';
import { CsvColumnConfig, CsvExportService } from '@app/core/services/csv-export/csv-export.service';
import { CsvExportFormattingService } from '@app/core/services/csv-export/csv-export-formatting.service';
import { IFpCorrelationData } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/interfaces/fp-correlation-data';
import { _, TranslateService } from '@ngx-translate/core';

@Injectable()
export class FpCorrelationGraphExportService {
  protected readonly csvExportService = inject(CsvExportService);
  protected readonly csvFormat = inject(CsvExportFormattingService);
  protected readonly dayjsLocale = inject(DayjsLocaleService);
  private readonly translate = inject(TranslateService);

  private localizePositionHeader = {
    [MeasurementSystem.Metric]: _(`@@chart-export-csv-header-position__m`),
    [MeasurementSystem.Imperial]: _(`@@chart-export-csv-header-position__ft`),
  };

  private getCsvColumnConfig(viewMeasurementSystem: MeasurementSystem): CsvColumnConfig<ICorrelationCurve>[] {
    return [
      {
        header: this.translate.instant(this.localizePositionHeader[viewMeasurementSystem]),
        value: row =>
          this.csvFormat.formatLength(row.position, measurementSystemLengthUnit_m_ft[viewMeasurementSystem], 6),
      },
      {
        header: this.translate.instant(_('@@chart-export-csv-header-time')),
        value: row => this.csvFormat.formatNumber(row.time),
      },
      {
        header: this.translate.instant(_('@@chart-export-csv-header-normalized-value')),
        value: row => this.csvFormat.formatNumber(row.normalizedValue),
      },
    ];
  }

  public exportCsv(
    popupMainData: IFpCorrelationData,
    data: ICorrelationCurve[],
    viewMeasurementSystem: MeasurementSystem
  ): void {
    const serialNo1 = popupMainData.loggerDeployment1.serialNo;
    const serialNo2 = popupMainData.loggerDeployment2.serialNo;
    const date = popupMainData.correlation.soundSignalStartAt;

    const formattedDate = this.dayjsLocale.dayjs(date).format(DayJSDateFormat.YYYYMMDD_HHmm);

    const columns = this.getCsvColumnConfig(viewMeasurementSystem);

    this.csvExportService.downloadCsv(`CorrelationGraph_${serialNo1}_${serialNo2}_${formattedDate}.csv`, data, columns);
  }
}
